import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

function BackupAdmin() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [expandedBackup, setExpandedBackup] = useState(null);
  const [backupContents, setBackupContents] = useState(null);
  const [contentsLoading, setContentsLoading] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  useEffect(() => { fetchBackups(); }, []);

  async function fetchBackups() {
    try {
      const res = await apiFetch('/api/backups');
      if (res.ok) setBackups(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function createBackup() {
    setCreating(true);
    setCreateResult(null);
    try {
      const res = await apiFetch('/api/backups/create', { method: 'POST' });
      const data = await res.json();
      setCreateResult(data);
      if (data.created) fetchBackups();
    } catch (err) {
      setCreateResult({ error: err.message });
    } finally {
      setCreating(false);
    }
  }

  async function downloadBackup(backupName) {
    try {
      const res = await apiFetch(`/api/backups/download?name=${encodeURIComponent(backupName)}`);
      if (res.ok) {
        const blob = await res.blob();
        const filename = backupName.split('/').pop();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      }
    } catch (err) { console.error(err); }
  }

  async function viewContents(backup) {
    if (expandedBackup === backup.name) {
      setExpandedBackup(null);
      setBackupContents(null);
      return;
    }
    setExpandedBackup(backup.name);
    setContentsLoading(true);
    try {
      const res = await apiFetch(`/api/backups/contents?name=${encodeURIComponent(backup.name)}`);
      if (res.ok) setBackupContents(await res.json());
    } catch (err) { console.error(err); }
    finally { setContentsLoading(false); }
  }

  async function restoreLayouts(backupName) {
    setRestoring(true);
    setRestoreResult(null);
    try {
      const res = await apiFetch(`/api/backups/restore-layouts?name=${encodeURIComponent(backupName)}`, { method: 'POST' });
      const data = await res.json();
      setRestoreResult(data.restored ? 'Layouts restored! Refreshing...' : 'Restore failed');
      if (data.restored) {
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (err) {
      setRestoreResult('Restore failed: ' + err.message);
    } finally {
      setRestoring(false);
      setRestoreConfirm(null);
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    if (dateStr.length === 10) {
      return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
        timeZone: 'America/Chicago', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      });
    }
    // Ad-hoc backups have timestamp: yyyy-MM-dd-HHmmss
    const parts = dateStr.match(/(\d{4})-(\d{2})-(\d{2})-(\d{2})(\d{2})(\d{2})/);
    if (parts) {
      const d = new Date(`${parts[1]}-${parts[2]}-${parts[3]}T${parts[4]}:${parts[5]}:${parts[6]}`);
      return d.toLocaleString('en-US', {
        timeZone: 'America/Chicago', weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    }
    return dateStr;
  }

  if (loading) {
    return <section className="card"><h2>Backup Admin</h2><p className="loading">Loading...</p></section>;
  }

  return (
    <section className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Backup Admin</h2>
        <button className="manage-btn" onClick={createBackup} disabled={creating}>
          {creating ? 'Creating...' : 'Create Backup Now'}
        </button>
      </div>

      {createResult && (
        <div className={`sfin-import-result ${createResult.created ? 'sfin-import-success' : 'sfin-import-error'}`} style={{ marginBottom: 8 }}>
          <span>
            {createResult.created
              ? `Backup created: ${createResult.fileCount} files`
              : `Error: ${createResult.error || 'Failed'}`}
          </span>
          <button className="modal-close" onClick={() => setCreateResult(null)}
            style={{ marginLeft: 8, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}>&times;</button>
        </div>
      )}

      {restoreResult && (
        <div className={`sfin-import-result ${restoreResult.includes('Restored') ? 'sfin-import-success' : 'sfin-import-error'}`} style={{ marginBottom: 8 }}>
          <span>{restoreResult}</span>
        </div>
      )}

      <p style={{ color: '#71717a', fontSize: 13, margin: '0 0 12px' }}>
        Daily backups at 5 AM CST. Click a backup to view contents.
      </p>

      {backups.length === 0 && <p style={{ color: '#71717a', fontSize: 13 }}>No backups yet.</p>}

      <div className="ba-backup-list">
        {backups.map((b) => (
          <div key={b.name}>
            <div className="ba-backup-row" onClick={() => viewContents(b)}>
              <span className="ba-backup-icon">{expandedBackup === b.name ? '▼' : '▶'}</span>
              <span className="ba-backup-date">{formatDate(b.date)}</span>
              <span className="ba-backup-name">{b.name}</span>
              <button
                className="manage-btn"
                onClick={(e) => { e.stopPropagation(); downloadBackup(b.name); }}
                style={{ fontSize: 11, padding: '3px 8px', marginLeft: 'auto', background: '#2a2b35' }}
              >
                Download
              </button>
              <button
                className="manage-btn"
                onClick={(e) => { e.stopPropagation(); setRestoreConfirm(b.name); }}
                style={{ fontSize: 11, padding: '3px 8px' }}
              >
                Restore Layouts
              </button>
            </div>

            {expandedBackup === b.name && (
              <div className="ba-contents">
                {contentsLoading && <p className="loading" style={{ fontSize: 13 }}>Loading contents...</p>}
                {!contentsLoading && backupContents && (
                  <div className="ba-file-list">
                    {backupContents.files.map((f) => (
                      <div key={f.name} className="ba-file-row">
                        <span className="ba-file-name">{f.name}</span>
                        <span className="ba-file-size">{(f.size / 1024).toFixed(1)} KB</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Restore Confirmation Modal */}
      {restoreConfirm && (
        <div className="modal-overlay" onClick={() => setRestoreConfirm(null)}>
          <div className="modal-content logout-confirm" onClick={(e) => e.stopPropagation()}>
            <h2>Restore Layouts?</h2>
            <p className="logout-confirm-text">
              This will overwrite the current layouts with the backup from:<br />
              <strong>{restoreConfirm}</strong><br /><br />
              The page will reload after restoring. This cannot be undone.
            </p>
            <div className="logout-confirm-actions">
              <button className="logout-confirm-cancel" onClick={() => setRestoreConfirm(null)}>Cancel</button>
              <button className="logout-confirm-yes" onClick={() => restoreLayouts(restoreConfirm)} disabled={restoring}>
                {restoring ? 'Restoring...' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default BackupAdmin;
